import { assessmentSerializer } from '@/providers/serializers/content/assessment';
import { collectionSerializer } from '@/providers/serializers/content/collection';
import { courseSerializer } from '@/providers/serializers/content/course';
import { unitSerializer } from '@/providers/serializers/content/unit';
import { lessonSerializer } from '@/providers/serializers/content/lesson';
import { questionSerializer } from '@/providers/serializers/content/question';
import { resourceSerializer } from '@/providers/serializers/content/resource';
import { rubricSerializer } from '@/providers/serializers/content/rubric';
import { DEFAULT_CATALOG_STRING, TAXONOMY_LEVELS, DEFAULT_IMAGES_PATH } from '@/utils/constants';
import { taxonomySerializer } from '../content/taxonomy';
import { sessionService } from '@/providers/services/auth/session';
import { competencySerializer } from '../competency/competency';
import { offlineActivitySerializer } from '../content/offlineActivity';

export class SearchSerializer {

  static get instance() {
    return this.INSTANCE;
  }

  private static INSTANCE = new SearchSerializer();



  public serializeAggregation(payload: any, contentType: string) {
    const result: any = [];
    const aggregation = payload.aggregations ? payload.aggregations : [];
    if (Object.keys(DEFAULT_CATALOG_STRING[contentType]).length) {
      Object.keys(DEFAULT_CATALOG_STRING[contentType]).map((item: any) => {
        const normalizeText: any = DEFAULT_CATALOG_STRING[contentType][item];
        const availableAggregation = aggregation.find((aggItem: any) => aggItem.key === item);
        normalizeText.value = availableAggregation ? availableAggregation.doc_count : 0;
        result.push(normalizeText);
      });
    }
    return result;
  }

  public serializeLearningMapData(learningMapData: any) {
    const learningMapContents = learningMapData.contents;
    const assessmentContents = learningMapContents.assessment;
    const collectionContents = learningMapContents.collection;
    const courseContents = learningMapContents.course;
    const lessonContents = learningMapContents.lesson;
    const questionContents = learningMapContents.question;
    const resourceContents = learningMapContents.resource;
    const unitContents = learningMapContents.unit;
    const rubricContents = learningMapContents.rubric;
    const offlineActivity = learningMapContents.offlineActivity;
    const serializedAssessmentContents = assessmentContents.searchResults.map((assessment: any) => {
      return assessmentSerializer.serializeAssessment(assessment);
    });
    const serializedCollectionContents = collectionContents.searchResults.map((collection: any) => {
      return collectionSerializer.serializeCollection(collection);
    });

    const serializedCourseContents = courseContents.searchResults.map((course: any) => {
      return courseSerializer.serializeCourse(course);
    });

    const serializedQuestionContents = questionContents.searchResults.map((question: any) => {
      return questionSerializer.serializeQuestion(question);
    });

    const serializedResourceContents = resourceContents.searchResults.map((resource: any) => {
      return resourceSerializer.serializeResource(resource);
    });

    const serializedUnitContents = unitContents.searchResults.map((unit: any) => {
      return unitSerializer.serializeUnit(unit);
    });

    const serializedLessonContents = lessonContents.searchResults.map((lesson: any) => {
      return lessonSerializer.serializeLesson(lesson);
    });

    const serializedRubricContents = rubricContents.searchResults.map((rubric: any) => {
      return rubricSerializer.serializeRubric(rubric);
    });

    const serializedOfflineContents = offlineActivity.searchResults.map((offline: any) => {
      return offlineActivitySerializer.normalizeOfflieActivity(offline);
    });
    const signatureContents = learningMapData.signatureContents;

    const signatureAssessments = signatureContents.assessments;
    const signatureCollections = signatureContents.collections;
    const serializedSignatureAssessments = signatureAssessments.map((assessment: any) => {
      return assessmentSerializer.serializeSignatureAssessment(assessment);
    });

    const serializedSignatureCollections = signatureCollections.map((collection: any) => {
      return collectionSerializer.serializeSignatureCollection(collection);
    });

    const serializedLearningMapData = {
      contents: {
        collection: serializedCollectionContents,
        assessment: serializedAssessmentContents,
        course: serializedCourseContents,
        unit: serializedUnitContents,
        lesson: serializedLessonContents,
        resource: serializedResourceContents,
        question: serializedQuestionContents,
        rubric: serializedRubricContents,
        offlineActivity: serializedOfflineContents,
      },
      totalCounts: {
        collection: learningMapContents.collection.totalHitCount,
        assessment: learningMapContents.assessment.totalHitCount,
        course: learningMapContents.course.totalHitCount,
        unit: learningMapContents.unit.totalHitCount,
        lesson: learningMapContents.lesson.totalHitCount,
        resource: learningMapContents.resource.totalHitCount,
        question: learningMapContents.question.totalHitCount,
        rubric: learningMapContents.rubric.totalHitCount,
        offlineActivity: learningMapContents.offlineActivity.totalHitCount,
      },
      signatureAssessments: serializedSignatureAssessments,
      signatureCollections: serializedSignatureCollections,
      prerequisites: competencySerializer.serializePrerequisites(learningMapData.prerequisites),
    };
    return serializedLearningMapData;
  }

  /**
   * @function normalizeSearchLearningMapsContentInfo
   * Serialize each content type from the learning map API
   */
  public normalizeSearchLearningMapsContentInfo(contents: any) {
    const serializedContentData: any = {};
    const assessmentData: any = [];
    const collectionData: any = [];
    const courseData: any = [];
    const resourceData: any = [];
    const questionData: any = [];
    const unitData: any = [];
    const lessonData: any = [];
    const offlineActivityData: any = [];
    if (contents.assessment) {
      contents.assessment.searchResults.map((assessment: any) => {
        const assessmentInfo = assessmentSerializer.normalizeAssessment(assessment);
        assessmentInfo.id = assessment.id;
        assessmentInfo.description = assessment.learningObjective;
        assessmentInfo.creator = taxonomySerializer.normalizeOwner(assessment.creator);
        assessmentInfo.owner = taxonomySerializer.normalizeOwner(assessment.user);
        assessmentInfo.standards = taxonomySerializer
          .normalizeTaxonomyArray(
            assessment.taxonomy,
            TAXONOMY_LEVELS.ASSESSMENT,
          );
        assessmentData.push(assessmentInfo);
      });
    }

    if (contents.collection) {
      contents.collection.searchResults.map((collection: any) => {
        const collectionInfo = collectionSerializer.normalizeCollection(collection);
        collectionInfo.id = collection.id;
        collectionInfo.description = collection.learningObjective;
        collectionInfo.creator = taxonomySerializer.normalizeOwner(collection.creator);
        collectionInfo.owner = taxonomySerializer.normalizeOwner(collection.user);
        collectionInfo.standards = taxonomySerializer
          .normalizeTaxonomyArray(
            collection.taxonomy,
            TAXONOMY_LEVELS.COLLECTION,
          );
        collectionData.push(collectionInfo);
      });
    }

    if (contents.offlineActivity) {
      contents.offlineActivity.searchResults.map((offlineActivity: any) => {
        const offlineActivityInfo = this.normalizeOfflineActivity(
          offlineActivity,
        );
        offlineActivityInfo.id = offlineActivity.id;
        offlineActivityInfo.description = offlineActivity.learningObjective;
        offlineActivityInfo.creator = taxonomySerializer.normalizeOwner(
          offlineActivity.creator,
        );
        offlineActivityInfo.owner = taxonomySerializer.normalizeOwner(
          offlineActivity.user,
        );
        offlineActivityInfo.standards = taxonomySerializer
          .normalizeTaxonomyArray(
            offlineActivity.taxonomy,
            TAXONOMY_LEVELS.COLLECTION,
          );
        offlineActivityData.push(offlineActivityInfo);
      });
    }

    if (contents.course) {
      contents.course.searchResults.map((course: any) => {
        const courseInfo: any = courseSerializer.serializeCourse(course);
        courseInfo.id = course.id;
        courseInfo.description = course.description;
        courseInfo.creator = course.creator
          ? taxonomySerializer.normalizeOwner(course.creator)
          : {};
        courseInfo.owner = course.owner
          ? taxonomySerializer.normalizeOwner(course.owner)
          : {};
        courseInfo.standards = taxonomySerializer
          .normalizeTaxonomyArray(
            course.taxonomy,
            TAXONOMY_LEVELS.COURSE,
          );
        courseData.push(courseInfo);
      });
    }

    if (contents.resource) {
      contents.resource.searchResults.map((resource: any) => {
        const resourceInfo: any = resourceSerializer.normalizeResource(resource);
        resourceInfo.id = resource.id;
        resourceInfo.description = resource.description;
        resourceInfo.creator = resource.creator
          ? taxonomySerializer.normalizeOwner(resource.creator)
          : {};
        resourceInfo.owner = resource.user
          ? taxonomySerializer.normalizeOwner(resource.user)
          : {};
        resourceInfo.standards = taxonomySerializer
          .normalizeTaxonomyArray(
            resource.taxonomy,
            TAXONOMY_LEVELS.RESOURCE,
          );
        resourceData.push(resourceInfo);
      });
    }

    if (contents.question) {
      contents.question.searchResults.map((question: any) => {
        const questionInfo = questionSerializer.normalizeQuestion(question);
        questionInfo.id = question.id;
        questionInfo.description = question.description;
        questionInfo.creator = taxonomySerializer.normalizeOwner(question.creator);
        questionInfo.owner = taxonomySerializer.normalizeOwner(question.user);
        questionInfo.standards = taxonomySerializer
          .normalizeTaxonomyArray(
            question.taxonomy,
            TAXONOMY_LEVELS.QUESTION,
          );
        questionData.push(questionInfo);
      });
    }

    if (contents.unit) {
      contents.unit.searchResults.map((unit: any) => {
        const unitInfo: any = unitSerializer.serializeUnit(unit);
        unitInfo.id = unit.id;
        unitInfo.description = unit.learningObjective;
        unitInfo.creator = unit.creator
          ? taxonomySerializer.normalizeOwner(unit.creator)
          : {};
        unitInfo.owner = unit.owner
          ? taxonomySerializer.normalizeOwner(unit.owner)
          : {};
        unitInfo.standards = taxonomySerializer
          .normalizeTaxonomyArray(
            unitInfo.taxonomy,
            TAXONOMY_LEVELS.QUESTION,
          );
        unitData.push(unitInfo);
      });
    }

    if (contents.lesson) {
      contents.lesson.searchResults.map((lesson: any) => {
        const lessonInfo: any = lessonSerializer.serializeLesson(lesson);
        lessonInfo.id = lesson.id;
        lessonInfo.description = lesson.learningObjective;
        lessonInfo.creator = lesson.creator
          ? taxonomySerializer.normalizeOwner(lesson.creator)
          : {};
        lessonInfo.owner = lesson.owner
          ? taxonomySerializer.normalizeOwner(lesson.owner)
          : {};
        lessonInfo.standards = taxonomySerializer
          .normalizeTaxonomyArray(
            lessonInfo.taxonomy,
            TAXONOMY_LEVELS.QUESTION,
          );
        lessonData.push(lessonInfo);
      });
    }

    serializedContentData.assessment = assessmentData;
    serializedContentData.collection = collectionData;
    serializedContentData.course = courseData;
    serializedContentData.resource = resourceData;
    serializedContentData.question = questionData;
    serializedContentData.unit = unitData;
    serializedContentData.lesson = lessonData;
    serializedContentData.offlineActivity = offlineActivityData;
    serializedContentData.totalHitCount = {
      assessment: contents.assessments ? contents.assessments.stats ? contents.assessments.stats.totalHitCount : 0 : 0,
      assessmentExternal: contents.externalAssessments ? contents.externalAssessments.totalHitCount : 0,
      collectionExternal: contents.externalCollections ? contents.externalCollections.totalHitCount : 0,
      collection: contents.collections ? contents.collections.stats ? contents.collections.stats.totalHitCount : 0 : 0,
      offlineActivity: contents.offlineActivities ? contents.offlineActivities.stats
        ? contents.offlineActivities.stats.totalHitCount : 0 : 0,
      question: contents.questions ? contents.questions.stats ? contents.questions.stats.totalHitCount : 0 : 0,
      resource: contents.resources ? contents.resources.stats ? contents.resources.stats.totalHitCount : 0 : 0,
      unit: contents.units ? contents.units.stats ? contents.units.stats.totalHitCount : 0 : 0,
      lesson: contents.lessons ? contents.lessons.stats ? contents.lessons.stats.totalHitCount : 0 : 0,
      course: contents.courses ? contents.courses.stats ? contents.courses.stats.totalHitCount : 0 : 0,
    },
      serializedContentData.maxTotalHitCount = Math.max(
        contents.assessments ? contents.assessments.stats ? contents.assessments.stats.totalHitCount : 0 : 0,
        contents.externalAssessments ? contents.externalAssessments.totalHitCount : 0,
        contents.externalCollections ? contents.externalCollections.totalHitCount : 0,
        contents.collections ? contents.collections.stats
          ? contents.collections.stats.totalHitCount : 0 : 0,
        contents.offlineActivities ? contents.offlineActivities.stats
          ? contents.offlineActivities.stats.totalHitCount : 0 : 0,
        contents.questions ? contents.questions.stats ? contents.questions.stats.totalHitCount : 0 : 0,
        contents.resources ? contents.resources.stats ? contents.resources.stats.totalHitCount : 0 : 0,
      );
    return serializedContentData;
  }

  public normalizeSearchCapLearningMapsContentInfo(contents: any) {
    const serializedContentData: any = {};
    const assessmentData: any = [];
    const collectionData: any = [];
    const courseData: any = [];
    const resourceData: any = [];
    const questionData: any = [];
    const unitData: any = [];
    const lessonData: any = [];
    const offlineActivityData: any = [];
    const challengingQuestionData: any = [];
    const diagnosticAssessmentsData: any = [];
    const diagnosticQuestionsData: any = [];
    const externalAssessmentsData: any = [];
    const externalCollectionsData: any = [];
    const practiceProblemsData: any = [];
    const rubricData: any = [];
    const signatureAssessmentsData: any = [];
    const signatureCollectionsData: any = [];
    const solvedExamplesData: any = [];

    if (contents.assessments && contents.assessments.searchResults) {
      contents.assessments.searchResults.map((assessment: any) => {
        const assessmentInfo = assessmentSerializer.normalizeAssessment(assessment);
        assessmentData.push(this.updateRestOfData(assessmentInfo, assessment));
      });
    }

    if (contents.collections && contents.collections.searchResults) {
      contents.collections.searchResults.map((collection: any) => {
        const collectionInfo = collectionSerializer.normalizeCollection(collection);
        collectionData.push(this.updateRestOfData(collectionInfo, collection));
      });
    }

    if (contents.offlineActivities && contents.offlineActivities.searchResults) {
      contents.offlineActivities.searchResults.map((offlineActivity: any) => {
        const offlineActivityInfo = this.normalizeOfflineActivity(
          offlineActivity);
        offlineActivityData.push(this.updateRestOfData(offlineActivityInfo, offlineActivity));
      });
    }

    if (contents.courses && contents.courses.searchResults) {
      contents.courses.searchResults.map((course: any) => {
        const courseInfo: any = courseSerializer.serializeCourse(course);
        courseData.push(this.updateRestOfData(courseInfo, course));
      });
    }

    if (contents.resources && contents.resources.searchResults) {
      contents.resources.searchResults.map((resource: any) => {
        const resourceInfo: any = resourceSerializer.normalizeResource(resource);
        resourceData.push(this.updateRestOfData(resourceInfo, resource));
      });
    }

    if (contents.questions && contents.questions.searchResults) {
      contents.questions.searchResults.map((question: any) => {
        const questionInfo = questionSerializer.normalizeQuestion(question);
        questionData.push(this.updateRestOfData(questionInfo, question));
      });
    }

    if (contents.units && contents.units.searchResults) {
      contents.units.searchResults.map((unit: any) => {
        const unitInfo: any = unitSerializer.serializeUnit(unit);
        unitData.push(this.updateRestOfData(unitInfo, unit));
      });
    }

    if (contents.lessons && contents.lessons.searchResults) {
      contents.lessons.searchResults.map((lesson: any) => {
        const lessonInfo: any = lessonSerializer.serializeLesson(lesson);
        lessonData.push(this.updateRestOfData(lessonInfo, lesson));
      });
    }

    if (contents.challengingQuestions && contents.challengingQuestions.searchResults) {
      contents.challengingQuestions.searchResults.map((challenge: any) => {
        const challengingInfo: any = lessonSerializer.serializeLesson(challenge);
        challengingQuestionData.push(this.updateRestOfData(challengingInfo, challenge));
      });
    }

    if (contents.diagnosticAssessments && contents.diagnosticAssessments.searchResults) {
      contents.diagnosticAssessments.searchResults.map((challenge: any) => {
        const challengingInfo: any = lessonSerializer.serializeLesson(challenge);
        diagnosticAssessmentsData.push(this.updateRestOfData(challengingInfo, challenge));
      });
    }

    if (contents.diagnosticQuestions && contents.diagnosticQuestions.searchResults) {
      contents.diagnosticQuestions.searchResults.map((challenge: any) => {
        const challengingInfo: any = lessonSerializer.serializeLesson(challenge);
        diagnosticQuestionsData.push(this.updateRestOfData(challengingInfo, challenge));
      });
    }

    if (contents.externalAssessments && contents.externalAssessments.searchResults) {
      contents.externalAssessments.searchResults.map((challenge: any) => {
        const challengingInfo: any = lessonSerializer.serializeLesson(challenge);
        externalAssessmentsData.push(this.updateRestOfData(challengingInfo, challenge));
      });
    }

    if (contents.externalCollections && contents.externalCollections.searchResults) {
      contents.externalCollections.searchResults.map((challenge: any) => {
        const challengingInfo: any = lessonSerializer.serializeLesson(challenge);
        externalCollectionsData.push(this.updateRestOfData(challengingInfo, challenge));
      });
    }

    if (contents.practiceProblems && contents.practiceProblems.searchResults) {
      contents.practiceProblems.searchResults.map((challenge: any) => {
        const challengingInfo: any = lessonSerializer.serializeLesson(challenge);
        practiceProblemsData.push(this.updateRestOfData(challengingInfo, challenge));
      });
    }

    if (contents.rubrics && contents.rubrics.searchResults) {
      contents.rubrics.searchResults.map((challenge: any) => {
        const challengingInfo: any = lessonSerializer.serializeLesson(challenge);
        rubricData.push(this.updateRestOfData(challengingInfo, challenge));
      });
    }

    if (contents.signatureAssessments && contents.signatureAssessments.searchResults) {
      contents.signatureAssessments.searchResults.map((challenge: any) => {
        const challengingInfo: any = assessmentSerializer.serializeSignatureAssessment(challenge);
        signatureAssessmentsData.push(this.updateRestOfData(challengingInfo, challenge));
      });
    }

    if (contents.signatureCollections && contents.signatureCollections.searchResults) {
      contents.signatureCollections.searchResults.map((challenge: any) => {
        const challengingInfo: any = collectionSerializer.serializeSignatureCollection(challenge);
        signatureCollectionsData.push(this.updateRestOfData(challengingInfo, challenge));
      });
    }

    if (contents.solvedExamples && contents.solvedExamples.searchResults) {
      contents.solvedExamples.searchResults.map((challenge: any) => {
        const challengingInfo: any = lessonSerializer.serializeLesson(challenge);
        solvedExamplesData.push(this.updateRestOfData(challengingInfo, challenge));
      });
    }

    serializedContentData.assessment = assessmentData;
    serializedContentData.collection = collectionData;
    serializedContentData.course = courseData;
    serializedContentData.resource = resourceData;
    serializedContentData.question = questionData;
    serializedContentData.unit = unitData;
    serializedContentData.lesson = lessonData;
    serializedContentData.offlineActivity = offlineActivityData;
    serializedContentData.rubric = rubricData;
    serializedContentData.challengingQuestion = challengingQuestionData;
    serializedContentData.diagnosticAssessments = diagnosticAssessmentsData;
    serializedContentData.diagnosticQuestions = diagnosticQuestionsData;
    serializedContentData.externalAssessments = externalAssessmentsData;
    serializedContentData.externalCollections = externalCollectionsData;
    serializedContentData.practiceProblems = practiceProblemsData;
    serializedContentData.signatureAssessments = signatureAssessmentsData;
    serializedContentData.signatureCollections = signatureCollectionsData;
    serializedContentData.solvedExamples = solvedExamplesData;

    serializedContentData.totalCounts = {
      assessment: contents.assessments ? contents.assessments.stats ? contents.assessments.stats.totalHitCount : 0 : 0,
      assessmentExternal: contents.externalAssessments ? contents.externalAssessments.totalHitCount : 0,
      collectionExternal: contents.externalCollections ? contents.externalCollections.totalHitCount : 0,
      collection: contents.collections ? contents.collections.stats ? contents.collections.stats.totalHitCount : 0 : 0,
      offlineActivity: contents.offlineActivities ? contents.offlineActivities.stats
        ? contents.offlineActivities.stats.totalHitCount : 0 : 0,
      question: contents.questions ? contents.questions.stats ? contents.questions.stats.totalHitCount : 0 : 0,
      resource: contents.resources ? contents.resources.stats ? contents.resources.stats.totalHitCount : 0 : 0,
      unit: contents.units ? contents.units.stats ? contents.units.stats.totalHitCount : 0 : 0,
      lesson: contents.lessons ? contents.lessons.stats ? contents.lessons.stats.totalHitCount : 0 : 0,
      course: contents.courses ? contents.courses.stats ? contents.courses.stats.totalHitCount : 0 : 0,
      rubric: contents.rubrics ? contents.rubrics.stats ? contents.rubrics.stats.totalHitCount : 0 : 0,
    };
    return serializedContentData;
  }

  /**
   * Normalize a offline Activity
   * @param {*} offlineActivityData
   * @returns {offline activity}
   */
  public normalizeOfflineActivity(collectionData: any) {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const basePath = contentCdnUrl;
    const appRootPath = window.location.href; // configuration appRootPath
    const userBasePath = cdnUrls.user_cdn_url;

    const thumbnailUrl = collectionData.thumbnail
      ? basePath + collectionData.thumbnail
      : DEFAULT_IMAGES_PATH.offlineActivity;
    const userThumbnailUrl = collectionData.userProfileImage
      ? userBasePath + collectionData.userProfileImage
      : DEFAULT_IMAGES_PATH.profile;
    const creatorThumbnailUrl = collectionData.creatorProfileImage
      ? userBasePath + collectionData.creatorProfileImage
      : DEFAULT_IMAGES_PATH.profile;
    const taxonomyInfo =
      (collectionData.taxonomySet &&
        collectionData.taxonomySet.curriculum &&
        collectionData.taxonomySet.curriculum.curriculumInfo) ||
      [];

    const course = collectionData.course || {};
    return {
      id: collectionData.id,
      title: collectionData.title,
      description: collectionData.description,
      type: collectionData.type ? collectionData.type : collectionData.format,
      thumbnailUrl,
      standards: taxonomySerializer.normalizeTaxonomyArray(collectionData, false),
      publishStatus: collectionData.publishStatus,
      learningObjectives: collectionData.languageObjective,
      resourceCount: collectionData.resourceCount || 0,
      questionCount: collectionData.questionCount || 0,
      remixCount: collectionData.scollectionRemixCount || 0,
      course: course.title,
      courseId: course.id,
      isVisibleOnProfile: collectionData.profileUserVisibility,
      owner: {
        id: collectionData.gooruUId,
        firstName: collectionData.userFirstName,
        lastName: collectionData.userLastName,
        avatarUrl: userThumbnailUrl,
        username: collectionData.usernameDisplay,
      },
      creator: {
        id: collectionData.creatorId,
        firstName: collectionData.creatorFirstname,
        lastName: collectionData.creatorLastname,
        avatarUrl: creatorThumbnailUrl,
        username: collectionData.creatornameDisplay,
      },
      taxonomySet: collectionData.taxonomySet,
      createdDate: collectionData.addDate,
      collaboratorIDs: collectionData.collaboratorIds,
      grade: collectionData.grade,
      instructionalModel: collectionData.instructionalMethod,
      lastModified: collectionData.lastModified,
      lastModifiedBy: collectionData.lastModifiedBy,
      license: collectionData.license,
      audience: collectionData.audience,
      keyPoints: collectionData.keyPoints,
      efficacy: collectionData.efficacy ? collectionData.efficacy : null,
      relevance: collectionData.relevance ? collectionData.relevance : null,
      engagement: collectionData.engagement ? collectionData.engagement : null,
    };
  }

  /**
   * Normalize the Learning Map Competency
   *
   * @param payload is the endpoint response in JSON format
   * @returns {LearningMapCompetencyData}
   */
  public normalizeSearchlearningMapCompetency(payload: any) {
    const microCompetencyData: any = [];
    const competencyData: any = [];
    const serializedCompetencyData = {
      totalHitCount: 0,
      competencyInfo: [],
    };
    const microComptencyLevelPattern = 'learning_target_level_';
    if (payload) {
      serializedCompetencyData.totalHitCount = payload.totalHitCount || 0;
      payload.stats.map((competency: any) => {
        if (competency.type.includes(microComptencyLevelPattern)) {
          /// micro-competency
          const storedMicroCompetencies =
            microCompetencyData[`${competency.parentId}`] || [];
          storedMicroCompetencies[
            competency.sequenceId - 1
          ] = this.normalizeLearningMapCompetencyData(competency);
          microCompetencyData[
            `${competency.parentId}`
          ] = storedMicroCompetencies;
        } else {
          competencyData.push(
            this.normalizeLearningMapCompetencyData(competency),
          );
        }
      });
      serializedCompetencyData.competencyInfo = this.sequenceLearningMapCompetencyItems(
        competencyData,
        microCompetencyData,
      );
    }
    return serializedCompetencyData;
  }

  /**
   * @function sequenceLearningMapCompetencyItems
   * To sequence the separated competency and micro-competency data
   * @param competencyData
   * @param microCompetencyData
   * @return sequencedCompetencyData
   */
  public sequenceLearningMapCompetencyItems(competencyData: any, microCompetencyData: any) {
    let sequencedCompetencyData: any = [];
    competencyData.map((competency: any) => {
      let microCompetencyInfo = null;
      sequencedCompetencyData.push(competency);
      microCompetencyInfo = microCompetencyData[`${competency.id}`] || null;
      if (microCompetencyInfo) {
        sequencedCompetencyData = sequencedCompetencyData.concat(
          microCompetencyInfo,
        );
      }
    });
    return sequencedCompetencyData;
  }

  /**
   * Restore missing competency properties with default values
   */
  public normalizeLearningMapCompetencyData(competency: any) {
    // Add missing data with empty content
    competency.prerequisites = competency.prerequisites || [];
    competency.title = competency.title || '';
    competency.code = competency.code || '';
    return competency;
  }

  /**
   * Normalizes a question
   * @param {*} result
   * @returns {Question}
   */
  public normalizeSearchlearningMapsContent(learningMapsContent: any) {
    const cdnUrls = sessionService.getCdnUrl();
    const basePath = cdnUrls.content_cdn_url;
    const signatureData = learningMapsContent.signatureContents;
    if (signatureData && signatureData.assessments) {
      signatureData.assessments.forEach((item: any) => {
        item.type = 'signatureAssessment';
        item.thumbnailUrl = item.thumbnail
          ? basePath + item.thumbnail
          : DEFAULT_IMAGES_PATH.assessment;
      });
    }
    if (signatureData && signatureData.collections) {
      signatureData.collections.forEach((item: any) => {
        item.type = 'signatureCollection';
        item.thumbnailUrl = item.thumbnail
          ? basePath + item.thumbnail
          : DEFAULT_IMAGES_PATH.collection;
      });
    }

    const returnObjects = {
      owner: {},
      title: learningMapsContent.title,
      code: learningMapsContent.code,
      gutCode: learningMapsContent.gutCode,
      contents: learningMapsContent.contents,
      prerequisites: learningMapsContent.prerequisites,
      subject: learningMapsContent.subject,
      course: learningMapsContent.course,
      domain: learningMapsContent.domain,
      signatureContents: signatureData,
      learningMapsContent: this.normalizeSearchLearningMapsContentInfo(
        learningMapsContent.contents,
      ),
    };
    return returnObjects;
  }
  private serializedPrerequisites(payload: any) {
    const prerequisitesData: any = [];
    if (payload.length) {
      payload.map((prerequitites: any) => {
        prerequisitesData.push({
          code: prerequitites.code,
          id: prerequitites.id,
          title: prerequitites.title,
        });
      });
    }
    return prerequisitesData;
  }

  private updateRestOfData(newObj: any, original: any) {
    newObj.id = original.id;
    newObj.description = original.learningObjective;
    newObj.creator = taxonomySerializer.normalizeOwner(original.creator);
    newObj.owner = taxonomySerializer.normalizeOwner(original.user);
    newObj.standards = taxonomySerializer
          .normalizeTaxonomyArray(
            original.taxonomy,
            TAXONOMY_LEVELS.ASSESSMENT,
          );
    return newObj;
  }
}

export const searchSerializer = SearchSerializer.instance;
